/**
 * App Layout Component - Trade Loop v2.0
 *
 * Main layout wrapper for authenticated pages
 * Includes Header and Bottom Navigation
 * Uses Supabase Realtime for instant notification and message updates
 * Includes pull-to-refresh functionality for all pages
 */

import { ReactNode, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/contexts/ToastContext";
import { useRealtimeNotifications } from "@/hooks/useRealtimeNotifications";
import { useRealtimeMessages } from "@/hooks/useRealtimeMessages";
import { usePullToRefresh } from "@/hooks/usePullToRefresh";
import { useAppBadge } from "@/hooks/useAppBadge";
import Header from "./Header";
import BottomNavigation from "./BottomNavigation";
import { RealtimeStatusIndicator } from "./RealtimeStatusIndicator";
import PullToRefreshIndicator from "@/components/PullToRefreshIndicator";
import { SwipeBackContainer } from "@/components/SwipeBackContainer";

interface AppLayoutProps {
  children: ReactNode;
  activeTab?: string;
  title?: string;
  backLink?: string;
  onRefresh?: () => Promise<void>;
  /**
   * When true, the main content area's bottom padding matches the bottom-nav
   * height (~61px + safe area) instead of the default 120px buffer. Use for
   * pages like the chat thread whose footer (e.g. message input) should sit
   * flush with the top of the bottom nav.
   */
  flushBottomNav?: boolean;
}

export default function AppLayout({
  children,
  activeTab,
  title,
  backLink,
  onRefresh,
  flushBottomNav = false,
}: AppLayoutProps) {
  const { user, sessionReady } = useAuth();
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();

  // Add app-layout class to body to enable viewport lock (prevents double scrollbar)
  // This only affects pages using AppLayout, not standalone pages like login/signup
  useEffect(() => {
    document.body.classList.add('app-layout');
    return () => {
      document.body.classList.remove('app-layout');
    };
  }, []);

  // Subscribe to Realtime notifications for instant updates
  // Only enable when session is ready for Supabase Realtime auth
  const notificationsRealtime = useRealtimeNotifications({
    userId: user?.id,
    enabled: !!user && sessionReady,
  });

  // Subscribe to Realtime messages for instant updates (replaces 30-second polling)
  // Only enable when session is ready for Supabase Realtime auth
  const messagesRealtime = useRealtimeMessages({
    userId: user?.id,
    enabled: !!user && sessionReady,
  });

  // Fetch unread notifications count
  // Realtime subscription handles instant updates via query invalidation
  const { data: notificationsData, isSuccess: notificationsCountLoaded } = useQuery({
    queryKey: ['unreadNotifications'],
    queryFn: async () => {
      const response = await axios.get('/api/workflows/notifications/unread-count', {
        withCredentials: true
      });
      return response.data;
    },
    enabled: !!user,
    staleTime: Infinity, // Realtime invalidates when needed
    refetchOnWindowFocus: true, // Refetch when user returns to tab
  });

  // Fetch unread messages count
  // Realtime subscription handles instant updates via query invalidation
  const { data: messagesData, isSuccess: messagesCountLoaded } = useQuery({
    queryKey: ['unreadMessages'],
    queryFn: async () => {
      const response = await axios.get('/api/workflows/messages/unread-count', {
        withCredentials: true
      });
      return response.data;
    },
    enabled: !!user,
    staleTime: Infinity, // Realtime invalidates when needed
    refetchOnWindowFocus: true, // Refetch when user returns to tab
  });

  const unreadNotifications = notificationsData?.data?.unreadCount || 0;
  const unreadMessages = messagesData?.data?.unreadCount || 0;

  // Mirror the two header badges onto the OS app icon. One number for the whole app, so
  // it is their sum — which is also what the server puts in `aps.badge`, keeping the
  // icon and the header from ever telling different stories. Deliberately gated on both
  // counts having actually loaded: they read 0 while in flight, and writing that would
  // blank a correct badge on every cold start.
  useAppBadge({
    unreadTotal: unreadNotifications + unreadMessages,
    enabled: !!user && notificationsCountLoaded && messagesCountLoaded,
  });

  // Pull-to-refresh functionality
  const handleRefresh = async () => {
    try {
      if (onRefresh) {
        // Use custom refresh handler provided by page
        await onRefresh();
      } else {
        // Default: invalidate all queries to refresh all data
        await queryClient.invalidateQueries();
      }
      showSuccess("Refreshed", "All data has been updated");
    } catch (error) {
      console.error("Failed to refresh:", error);
      showError("Refresh Failed", "Unable to refresh data. Please try again.");
    }
  };

  const {
    containerRef,
    pullDistance,
    isRefreshing,
    shouldTriggerRefresh,
  } = usePullToRefresh({
    onRefresh: handleRefresh,
  });

  // Combine realtime status for indicator (show worst status)
  const getOverallStatus = () => {
    if (notificationsRealtime.status === 'error' || messagesRealtime.status === 'error') {
      return 'error' as const;
    }
    if (notificationsRealtime.status === 'reconnecting' || messagesRealtime.status === 'reconnecting') {
      return 'reconnecting' as const;
    }
    if (notificationsRealtime.status === 'connecting' || messagesRealtime.status === 'connecting') {
      return 'connecting' as const;
    }
    if (notificationsRealtime.status === 'subscribed' && messagesRealtime.status === 'subscribed') {
      return 'subscribed' as const;
    }
    return 'disconnected' as const;
  };

  return (
    // height uses the dynamic viewport (100dvh) so the layout matches the
    // *visible* area on mobile browsers, where the URL bar makes 100vh taller
    // than what's on screen. Without this the fixed bottom nav overlaps the
    // bottom of the main content (e.g. the chat composer sits just below the
    // fold, unreachable). The h-screen class stays as a 100vh fallback for
    // browsers that don't support dvh (the inline dvh is simply ignored there).
    <div
      style={{ height: '100dvh' }}
      className="h-screen bg-gradient-to-br from-neutral-50 via-gray-50/50 to-white flex flex-col overflow-hidden"
    >
      {/* Header */}
      <div className="flex-shrink-0">
        <Header
          unreadCount={unreadNotifications}
          unreadMessages={unreadMessages}
          title={title}
          backLink={backLink}
          realtimeStatus={getOverallStatus()}
          reconnectAttempts={Math.max(
            notificationsRealtime.reconnectAttempts,
            messagesRealtime.reconnectAttempts
          )}
        />
      </div>

      {/* Main Content - Pages handle their own scrolling */}
      {/* '/signup' is deliberate — the page is gone but the path still renders
          a redirect. See SignupPathReferencesRetained.test.ts. */}
      <SwipeBackContainer
        excludedRoutes={['/dashboard', '/login', '/signup', '/', '/referrals', '/messages', '/profile']}
      >
        <main
          ref={containerRef}
          className="flex-1 overflow-y-auto h-full md:pt-[5px]"
          style={{
            // --app-header-offset (index.css) is the fixed chrome — Header +
            // status-bar safe area + any impersonation bar — plus ~10px so content
            // does not start flush against it.
            paddingTop: 'var(--app-header-offset)',
            // This element is the scroll container for every page, and its top
            // edge sits *behind* the fixed Header. Without scroll-padding-top,
            // scrollIntoView({ block: 'start' }) aligns the target with that
            // hidden edge and it ends up cut off under the header. This uses the
            // chrome's true height, not the padded offset above: the scrollport
            // should end where the header ends, leaving each target's own
            // scroll-margin-top as the only gap.
            scrollPaddingTop: 'var(--app-header-height)',
            paddingBottom: flushBottomNav
              ? 'calc(61px + env(safe-area-inset-bottom, 0px))'
              : 'calc(120px + env(safe-area-inset-bottom, 0px))'
          }}
        >
          <PullToRefreshIndicator
            pullDistance={pullDistance}
            isRefreshing={isRefreshing}
            shouldTriggerRefresh={shouldTriggerRefresh}
          />
          {children}
        </main>
      </SwipeBackContainer>

      {/* Bottom Navigation */}
      <BottomNavigation activeTab={activeTab} />

      {/* Dev-only Realtime Status (fixed position, hidden on mobile to prevent bouncing) */}
      {/* Disabled 2026-09-16 — no longer needed. Left commented so it can be flipped back on quickly if a realtime regression needs eyes. */}
      {/* {import.meta.env.DEV && (
        <div className="fixed bottom-24 right-4 z-50 bg-white/90 backdrop-blur-sm rounded-lg px-2 py-1 shadow-md border border-gray-200 hidden md:block">
          <RealtimeStatusIndicator
            status={getOverallStatus()}
            label="RT"
            reconnectAttempts={Math.max(
              notificationsRealtime.reconnectAttempts,
              messagesRealtime.reconnectAttempts
            )}
          />
        </div>
      )} */}
    </div>
  );
}
